import { useQuery } from "@apollo/client";
import { LoadingIndicator } from "@govlaunch/core";
import React, { useState } from "react";
import { IPlanTier } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import AreYouSure from "~/components/makers/AreYouSure";
import ChangePlansMutations from "~/components/makers/ChangePlansMutations";
import ComparePlansBeforeChanging from "~/components/makers/ComparePlansBeforeChanging";
import ConfirmDowngrade from "~/components/makers/ConfirmDowngrade";
import DowngradeSuccessful from "~/components/makers/DowngradeSuccessful";
import hasSufficedAllDowngradeRequirements from "~/components/makers/hasSufficedAllDowngradeRequirements";
import ShouldMeetAllRequirements from "~/components/makers/ShouldMeetAllRequirements";
import {
  FailedToCancelSubscription,
  FailedToCommitPlanChange,
  NoCurrentPlan,
  NoPlans,
  NoSelectedPlanToChange,
  NotAVendor,
} from "~/components/makers/ChangePlansAlerts";
import ChangePlanOptions from "~/components/makers/ChangePlanOptions";
import AvailablePlans from "~/lib/queries/AvailablePlans";
import {
  IAvailablePlansQuery,
  IAvailablePlansQueryVariables,
} from "~/lib/queries/__generated__/AvailablePlans.generated";

export default function ChangePlans() {
  const selfie = useSelfie();

  // will be "BASIC" when the user is "cancelling" the subscription. Otherwise, that will be a Stripe plan identifier
  const [selectedPlanToChange, setSelectedPlanToChange] = useState<string | null>(null);

  // this is used in the downgrade scenario where the user must compare what he's changing before actually confirming it
  const [changeStep, setChangeStep] = useState<
    "COMPARE_BOTH_PLANS" | "CONFIRM_CHANGES" | "MEET_REQUIREMENTS" | "TYPE_DOWNGRADE" | null
  >(null);

  const { data, loading } = useQuery<IAvailablePlansQuery, IAvailablePlansQueryVariables>(AvailablePlans);

  if (loading) {
    return <LoadingIndicator />;
  }

  const basicPlan = (data && data.availablePlans?.find((plan) => plan.tier === IPlanTier.Basic)) || null;
  const noPlans = !data || !data.availablePlans || data.availablePlans.length === 0;

  if (!selfie || selfie.__typename !== "VendorUser" || !selfie.company) {
    return <NotAVendor />;
  }

  if (noPlans || !basicPlan) {
    return <NoPlans />;
  }

  const currentPlan = data!.availablePlans.find((plan) => plan.stripePlan && plan.stripePlan.viewerIsSubscribed);

  if (!currentPlan || !currentPlan.stripePlan) {
    return <NoCurrentPlan />;
  }

  return (
    <ChangePlansMutations>
      {({
        changeSubscriptionPlan,
        isChangingPlans,
        failedToCommitPlanChange,
        cancelSubscription,
        isCancellingSubscription,
        cancelSubscriptionMutationResult,
        failedToCancelSubscription,
      }) => {
        if (!selfie.company) {
          return <NotAVendor />;
        }

        if (failedToCommitPlanChange) {
          return <FailedToCommitPlanChange />;
        }

        if (failedToCancelSubscription) {
          return <FailedToCancelSubscription />;
        }

        if (
          cancelSubscriptionMutationResult &&
          cancelSubscriptionMutationResult.cancelSubscription.subscription &&
          cancelSubscriptionMutationResult.cancelSubscription.subscription.__typename ===
            "ScheduledToCancelSubscription"
        ) {
          return (
            <DowngradeSuccessful
              willCancelAt={cancelSubscriptionMutationResult.cancelSubscription.subscription.willCancelAt}
            />
          );
        }

        if (selfie.company.subscription && selfie.company.subscription.__typename === "ScheduledToCancelSubscription") {
          return <DowngradeSuccessful willCancelAt={selfie.company.subscription.willCancelAt} />;
        }

        if (changeStep === "TYPE_DOWNGRADE") {
          return (
            <ConfirmDowngrade
              loading={isCancellingSubscription || isChangingPlans}
              currentPlan={currentPlan.stripePlan!}
              onConfirm={() => {
                if (selectedPlanToChange === "BASIC") {
                  cancelSubscription({});
                  return;
                }

                const changingToPlan = data!.availablePlans.find((plan) => {
                  return plan.stripePlan && plan.stripePlan.planId === selectedPlanToChange;
                });

                if (changingToPlan) {
                  changeSubscriptionPlan({
                    variables: {
                      stripePlanId: changingToPlan.stripePlan!.planId,
                    },
                  });
                }
              }}
              onCancel={() => setChangeStep(null)}
            />
          );
        }

        if (changeStep === "MEET_REQUIREMENTS") {
          const changingToPlan = data!.availablePlans.find((plan) => {
            return plan.stripePlan && plan.stripePlan.planId === selectedPlanToChange;
          });

          return (
            <ShouldMeetAllRequirements
              toPlan={changingToPlan && changingToPlan.stripePlan ? changingToPlan.stripePlan : "BASIC"}
              planUsage={selfie.company.planUsage}
              onCancel={() => setChangeStep(null)}
            />
          );
        }

        if (changeStep === "CONFIRM_CHANGES") {
          const changingToPlan = data!.availablePlans.find((plan) => {
            return plan.stripePlan && plan.stripePlan.planId === selectedPlanToChange;
          });

          return (
            <AreYouSure
              onConfirm={() => {
                if (!selfie.company) {
                  return;
                }

                setChangeStep(
                  hasSufficedAllDowngradeRequirements(
                    selectedPlanToChange === "BASIC"
                      ? "BASIC"
                      : changingToPlan && changingToPlan.stripePlan
                        ? changingToPlan.stripePlan
                        : "BASIC",
                    selfie.company.planUsage,
                  )
                    ? "TYPE_DOWNGRADE"
                    : "MEET_REQUIREMENTS",
                );
              }}
              onCancel={() => setChangeStep(null)}
            />
          );
        }

        if (changeStep === "COMPARE_BOTH_PLANS") {
          if (selectedPlanToChange === "BASIC") {
            return (
              <ComparePlansBeforeChanging
                fromPlan={currentPlan.stripePlan!}
                toPlan="BASIC"
                onConfirm={() => setChangeStep("CONFIRM_CHANGES")}
                onCancel={() => setChangeStep(null)}
              />
            );
          }

          const changingToPlan = data!.availablePlans.find((plan) => {
            return plan.stripePlan && plan.stripePlan.planId === selectedPlanToChange;
          });

          if (!changingToPlan || !changingToPlan.stripePlan) {
            return <NoSelectedPlanToChange />;
          }

          return (
            <ComparePlansBeforeChanging
              fromPlan={currentPlan.stripePlan!}
              toPlan={changingToPlan.stripePlan}
              onConfirm={() => setChangeStep("CONFIRM_CHANGES")}
              onCancel={() => setChangeStep(null)}
            />
          );
        }

        if (loading) {
          return <LoadingIndicator />;
        }

        return (
          <ChangePlanOptions
            vendorSlug={selfie.company.slug}
            basicPlan={basicPlan}
            selectedPlanToChange={selectedPlanToChange}
            setSelectedPlanToChange={setSelectedPlanToChange}
            stripePlans={data!.availablePlans.map((plan) => plan.stripePlan).filter(Boolean) as any}
            currentPlan={currentPlan.stripePlan!}
            changeSubscriptionPlan={changeSubscriptionPlan}
            isChangingPlans={isChangingPlans}
            moveToChangeStep={setChangeStep}
          />
        );
      }}
    </ChangePlansMutations>
  );
}
